import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import Form, {
    ButtonItem,
    SimpleItem,
    EmptyItem,
    ColCountByScreen,
    Label,
    GroupItem,
    TabbedItem,
    TabPanelOptions,
    Tab,
} from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { useDispatch, useSelector } from 'react-redux'
import { addClass } from 'app/redux/store/features/classes'
import { useSnackbar } from 'notistack'
import formatCurrency from 'app/utils/components/formatCurrency/formatCurrency'
import { IconButton, Stack, Typography, useMediaQuery } from '@mui/material'
import WestIcon from '@mui/icons-material/West'
import { classDateEditorOptions } from './ClassDatesInfoHint'
import utilServices from 'app/services/util-services'
import termServices from 'app/services/pages/courses/setup/termServices/termServices'
import { validateClassDates } from './validateClassDates'

const AddClassForm = (props) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { handleSubmit } = useForm()
    const formRef = useRef(null)
    const { loading, error } = useSelector((state) => state.classes)
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [labelLocation, setLabelLocation] = useState('left')
    const sm = useMediaQuery('(max-width:600px)')

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)
        if (error) props.handleError(error)
        else {
            enqueueSnackbar('Class added successfully', {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
            navigate(`/courses/setup/classes`, { state: { navigateFrom: 'add-class' } })
        }
    }, [loading])

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () => navigate(`/courses/setup/classes`, { state: { navigateFrom: 'cancel-add-class' } }),
    }

    const validationRules = {
        className: [{ type: 'required', message: 'Class Name is required.' }],
        dateStart: [{ type: 'required', message: 'Overall Date Start is required.' }],
        dateEnd: [{ type: 'required', message: 'Overall Date End is required.' }],
        bookingStart: [{ type: 'required', message: 'Booking Start is required.' }],
        bookingEnd: [{ type: 'required', message: 'Booking End is required.' }],
        termStart: [{ type: 'required', message: 'Study Start is required.' }],
        termEnd: [{ type: 'required', message: 'Study Ends is required.' }],
        termId: [{ type: 'required', message: 'Term is required.' }],
        subTitle: [{ type: 'required', message: 'Sub Title is required.' }],
        ageGroupId: [{ type: 'required', message: 'Age Group is required.' }],
        termInfo: [{ type: 'required', message: 'Term Info is required.' }],
        shortDesc: [{ type: 'required', message: 'Age Info is required.' }],
        description: [{ type: 'required', message: 'Description is required.' }],
        description2: [{ type: 'required', message: 'Description 2 is required.' }],
        classTypeId: [{ type: 'required', message: 'Class Type is required.' }],
    }

    const CurrencyEditorOptions = {
        format: (data) => formatCurrency(data),
    }

    const onAddClass = useCallback(async () => {
        setApiCallInProgress(true)
        const formInstance = formRef.current?.instance
        if (formInstance) {
            const validationResult = await formInstance.validate()
            const passed = validationResult !== false && validationResult?.isValid !== false
            if (!passed) {
                setApiCallInProgress(false)
                return
            }
        }
        const base = formInstance ? { ...formInstance.option('formData') } : { ...props.classData }
        const additionalEditor = formInstance?.getEditor?.('additionalTeacherIds')
        if (additionalEditor) {
            const val = additionalEditor.option('value')
            base.additionalTeacherIds = utilServices.sanitizeAdditionalTeacherIds(
                base,
                Array.isArray(val) ? val : []
            )
        } else {
            base.additionalTeacherIds = utilServices.sanitizeAdditionalTeacherIds(
                base,
                Array.isArray(base.additionalTeacherIds) ? base.additionalTeacherIds : []
            )
        }
        try {
            const term = await termServices.getTermLookupById(base.termId)
            const termIdStr = String(base.termId ?? '')
            const termBoundsOk =
                term &&
                termIdStr &&
                String(term.id ?? '').toLowerCase() === termIdStr.toLowerCase()
            const dateMsg = validateClassDates({
                dateStart: base.dateStart,
                dateEnd: base.dateEnd,
                bookingStart: base.bookingStart,
                bookingEnd: base.bookingEnd,
                termStart: base.termStart,
                termEnd: base.termEnd,
                termDateStart: termBoundsOk ? term.dateStart : undefined,
                termDateEnd: termBoundsOk ? term.dateEnd : undefined,
            })
            if (dateMsg) {
                enqueueSnackbar(dateMsg, {
                    variant: 'error',
                    anchorOrigin: { vertical: 'top', horizontal: 'right' },
                })
                setApiCallInProgress(false)
                return
            }
        } catch {
            enqueueSnackbar('Unable to validate dates against the term. Try again.', {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
            })
            setApiCallInProgress(false)
            return
        }
        dispatch(
            addClass({
                ...base,
                dateStart: utilServices.toUtcStartOfDayForApi(base.dateStart),
                dateEnd: utilServices.toUtcEndOfDayForApi(base.dateEnd),
                bookingStart: utilServices.toUtcStartOfDayForApi(base.bookingStart),
                bookingEnd: utilServices.toUtcEndOfDayForApi(base.bookingEnd),
                termStart: utilServices.toUtcStartOfDayForApi(base.termStart),
                termEnd: utilServices.toUtcEndOfDayForApi(base.termEnd),
            })
        )
    }, [props.classData, dispatch, enqueueSnackbar])

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/courses/setup/classes`, {
                                    state: { navigateFrom: 'cancel-add-class' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Add Class
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form onSubmit={handleSubmit(onAddClass)}>
                    <Form
                        ref={formRef}
                        mode='form'
                        formData={props.classData}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}>
                        <GroupItem>
                            <TabbedItem>
                                <TabPanelOptions deferRendering={false} />
                                <Tab title=' New Class'>
                                    <GroupItem>
                                        <ColCountByScreen lg={2} md={2} sm={1} xs={1} />
                                        <GroupItem caption='Details' cssClass='form-group-item'>
                                            <GroupItem cssClass='form-group-item-margin'>
                                                <SimpleItem
                                                    dataField='enableAutoSectionChat'
                                                    editorType='dxCheckBox'
                                                    colSpan={2}>
                                                    <Label text='Class-wide chat (single auto section)' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='termId'
                                                    editorType='dxSelectBox'
                                                    editorOptions={props.termEditorOptions}
                                                    validationRules={validationRules.termId}>
                                                    <Label text='Term' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='locationId'
                                                    editorType='dxSelectBox'
                                                    editorOptions={props.locationEditorOptions}>
                                                    <Label text='Location Name' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='className'
                                                    validationRules={validationRules.className}
                                                />
                                                <SimpleItem
                                                    dataField='classTypeId'
                                                    editorType='dxSelectBox'
                                                    validationRules={validationRules.classTypeId}
                                                    editorOptions={props.classTypeEditorOptions}>
                                                    <Label text='Class Type' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    editorType='dxSelectBox'
                                                    dataField='classGenderTypeId'
                                                    editorOptions={props.genderEditorOptions}>
                                                    <Label text='Class Gender Type' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='subTitle'
                                                    validationRules={validationRules.subTitle}
                                                />
                                                <SimpleItem
                                                    editorType='dxSelectBox'
                                                    dataField='ageGroupId'
                                                    validationRules={validationRules.ageGroupId}
                                                    editorOptions={props.ageGroupEditorOptions}>
                                                    <Label text='Age Group' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='validateMinimum'
                                                    editorType='dxCheckBox'
                                                    colSpan={2}>
                                                    <Label text='Validate Minimum Age' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='validateMaximum'
                                                    editorType='dxCheckBox'
                                                    colSpan={2}>
                                                    <Label text='Validate Maximum Age' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='shortDesc'
                                                    validationRules={validationRules.shortDesc}>
                                                    <Label text='Age Info' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='termInfo'
                                                    validationRules={validationRules.termInfo}>
                                                    <Label text='Term Info' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='classPrice'
                                                    editorType='dxNumberBox'
                                                    editorOptions={CurrencyEditorOptions}>
                                                    <Label text='Class Price' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    dataField='feeChargeBasis'
                                                    editorType='dxSelectBox'
                                                    editorOptions={{
                                                        items: [
                                                            { value: 0, text: 'Per participant' },
                                                            { value: 1, text: 'Flat fee (selected participants)' },
                                                            { value: 2, text: 'Flat fee (all family members)' },
                                                        ],
                                                        valueExpr: 'value',
                                                        displayExpr: 'text',
                                                        searchEnabled: false,
                                                    }}>
                                                    <Label text='Fee charge basis' />
                                                </SimpleItem>
                                                <SimpleItem dataField='comments' editorType='dxTextArea' />
                                                <SimpleItem dataField='canDeposit' editorType='dxCheckBox'>
                                                    <Label text='Deposit Enable' />
                                                </SimpleItem>
                                                <SimpleItem
                                                    editorType='dxNumberBox'
                                                    dataField='depositAmount'
                                                    editorOptions={CurrencyEditorOptions}>
                                                    <Label text='Deposit Amount' />
                                                </SimpleItem>
                                            </GroupItem>
                                        </GroupItem>
                                        <GroupItem>
                                            <GroupItem caption='Configuration' cssClass='form-group-item '>
                                                <GroupItem caption='Class dates' cssClass='form-group-item-margin'>
                                                    <SimpleItem
                                                        dataField='dateStart'
                                                        editorType='dxDateBox'
                                                        validationRules={validationRules.dateStart}
                                                        editorOptions={classDateEditorOptions('dateStart')}>
                                                        <Label text='Overall Date Start' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='dateEnd'
                                                        editorType='dxDateBox'
                                                        validationRules={validationRules.dateEnd}
                                                        editorOptions={classDateEditorOptions('dateEnd')}>
                                                        <Label text='Overall Date End' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='bookingStart'
                                                        editorType='dxDateBox'
                                                        validationRules={validationRules.bookingStart}
                                                        editorOptions={classDateEditorOptions('bookingStart')}>
                                                        <Label text='Booking Start' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='bookingEnd'
                                                        editorType='dxDateBox'
                                                        validationRules={validationRules.bookingEnd}
                                                        editorOptions={classDateEditorOptions('bookingEnd')}>
                                                        <Label text='Booking End' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='termStart'
                                                        editorType='dxDateBox'
                                                        validationRules={validationRules.termStart}
                                                        editorOptions={classDateEditorOptions('termStart')}>
                                                        <Label text='Study Start' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='termEnd'
                                                        editorType='dxDateBox'
                                                        validationRules={validationRules.termEnd}
                                                        editorOptions={classDateEditorOptions('termEnd')}>
                                                        <Label text='Study Ends' />
                                                    </SimpleItem>
                                                </GroupItem>
                                                <GroupItem cssClass='form-group-item-margin'>
                                                    <SimpleItem
                                                        dataField='newEnrolDays'
                                                        editorType='dxNumberBox'
                                                        editorOptions={{ showSpinButtons: 'true' }}>
                                                        <Label text='Delay New Enrol (Days after start date)' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='reEnrolDays'
                                                        editorType='dxNumberBox'
                                                        editorOptions={{ showSpinButtons: 'true' }}>
                                                        <Label text='Open ReEnrol (Days before end date)' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='prvClassId'
                                                        editorType='dxSelectBox'
                                                        editorOptions={props.prvClassEditorOptions}>
                                                        <Label text='ReEnrol From Class (Last term)' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='teacherId'
                                                        editorType='dxSelectBox'
                                                        editorOptions={props.teacherEditorOptions}>
                                                        <Label text='Teacher Name' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='additionalTeacherIds'
                                                        editorType='dxTagBox'
                                                        editorOptions={{
                                                            ...props.teacherEditorOptions,
                                                            showSelectionControls: true,
                                                            maxDisplayedTags: 3,
                                                        }}>
                                                        <Label text='Additional teachers' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='capacity'
                                                        editorType='dxNumberBox'
                                                        editorOptions={{ showSpinButtons: 'true' }}
                                                    />
                                                    <SimpleItem
                                                        dataField='txnDueDays'
                                                        editorType='dxNumberBox'
                                                        editorOptions={{ showSpinButtons: 'true' }}>
                                                        <Label text='Payment Due (Days)' />
                                                    </SimpleItem>
                                                    <SimpleItem dataField='registerCode'>
                                                        <Label text='Register Code' />
                                                    </SimpleItem>
                                                    <SimpleItem
                                                        dataField='description'
                                                        editorType='dxTextArea'
                                                        validationRules={validationRules.description}
                                                    />
                                                    <SimpleItem
                                                        dataField='description2'
                                                        editorType='dxTextArea'
                                                        validationRules={validationRules.description2}
                                                    />
                                                </GroupItem>
                                            </GroupItem>
                                        </GroupItem>
                                    </GroupItem>
                                </Tab>
                            </TabbedItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default AddClassForm
